import React from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { isoDateToMDY } from "../../../utils/common";
import { CreateEnrollment } from "./modals/CreateEnrollment";
import { EditEnrollment } from "./modals/EditEnrollment";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TabHeader } from "../shared/components/TabHeader";
import { TabSpinner } from "../shared/components/TabSpinner";
import { TabToolbar } from "../shared/components/TabToolbar";
import { Input } from "@/components/ui/input";
import { TabTable } from "../shared/components/TabTable";

const GET_ENROLLMENT_DATA = gql`
  query GetParticipantEnrollments($programNumber: Int!) {
    getParticipantEnrollments(programNumber: $programNumber) {
      id
      site
      enrollmentDate
      exitDate
      exitReason
      notes
      referralType
      referringIndividual
      referringAgency
      entryBy
      entryDate
      updatedBy
      updatedDate
    }
  }
`;

const DELETE_ENROLLMENT = gql`
  mutation DeleteEnrollment($id: Int!) {
    deleteEnrollment(id: $id) {
      message
    }
  }
`;

type EnrollmentRow = {
  id: number;
  site: string;
  enrollmentDate?: string;
  exitDate?: string | null;
  exitReason?: string | null;
  notes?: string | null;
  referralType?: string | null;
  referringIndividual?: string | null;
  referringAgency?: string | null;
  entryBy?: string | null;
  entryDate?: string | null;
  updatedBy?: string | null;
  updatedDate?: string | null;
};

export const Enrollment = ({ programNumber }: { programNumber: number }) => {
  const [showCreateEnrollment, setShowCreateEnrollment] = React.useState<{
    show: boolean;
    data: EnrollmentRow | null;
    method: "none" | "create" | "edit";
  }>({
    show: false,
    data: null,
    method: "none",
  });
  const [enrollmentToDelete, setEnrollmentToDelete] = React.useState<number | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const { loading, error, data, refetch } = useQuery(GET_ENROLLMENT_DATA, {
    variables: { programNumber },
    skip: !programNumber,
  });

  const [deleteEnrollment] = useMutation(DELETE_ENROLLMENT, {
    onCompleted: () => {
      refetch();
    },
  });

  const handleDeleteEnrollment = async () => {
    if (enrollmentToDelete) {
      await deleteEnrollment({ variables: { id: enrollmentToDelete } });
      setIsDeleteDialogOpen(false);
      setEnrollmentToDelete(null);
    }
  };

  if (loading) return <TabSpinner label="Loading enrollment..." />;
  if (error) return <p>Error loading enrollment data: {error.message}</p>;
  if (!data) return null;

  const enrollmentData: EnrollmentRow[] = (data.getParticipantEnrollments || []).filter((row: any) => {
    const term = search.toLowerCase();
    return (
      row.site?.toLowerCase().includes(term) ||
      row.referralType?.toLowerCase().includes(term) ||
      row.referringAgency?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="space-y-8">
      <TabHeader
        icon={Users}
        title="Enrollment"
        subtitle="Program enrollments and exits"
        action={
          <Button
            variant="outline"
            className="bg-blue-600 text-white hover:bg-blue-700 border-0"
            onClick={() =>
              setShowCreateEnrollment({ show: true, data: null, method: "create" })
            }
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Enrollment
          </Button>
        }
      />
      <TabToolbar
        left={
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search enrollments..."
            className="h-9 w-72"
          />
        }
      />

      <Card className="rounded-none border-0 shadow-none">
        <CardContent className="p-0">
          <div className="border-t border-b border-gray-200 overflow-hidden">
            <TabTable<EnrollmentRow>
              data={enrollmentData}
              emptyMessage="No enrollment records found."
              columns={[
                { title: "Enrollment Date", className: "w-[140px]", render: (row) => row.enrollmentDate ? isoDateToMDY(row.enrollmentDate) : "—" },
                { title: "Site", className: "w-[160px]", render: (row) => row.site || "—" },
                { title: "Referring Agency", className: "w-[200px]", render: (row) => row.referringAgency || "—" },
                { title: "Referring Individual", className: "w-[200px]", render: (row) => row.referringIndividual || "—" },
                { title: "Referral Type", className: "w-[160px]", render: (row) => row.referralType || "—" },
                { title: "Exit Date", className: "w-[140px]", render: (row) => row.exitDate ? isoDateToMDY(row.exitDate) : "—" },
                { title: "Notes", render: (row) => <div className="text-sm text-gray-700 leading-relaxed">{row.notes || "—"}</div> },
                { title: "Actions", className: "w-[150px]", render: (row) => (
                  <div className="flex items-center gap-2 justify-end pr-1">
                    <Button
                      variant="ghost"
                      className="text-blue-600 px-2"
                      onClick={() =>
                        setShowCreateEnrollment({
                          show: true,
                          data: row,
                          method: "edit",
                        })
                      }
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      className="text-red-600 px-2"
                      onClick={() => {
                        setEnrollmentToDelete(row.id);
                        setIsDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )},
              ]}
            />
          </div>
        </CardContent>
      </Card>

      {showCreateEnrollment.method === "create" && (
        <CreateEnrollment
          participant={programNumber}
          onClose={() => {
            setShowCreateEnrollment({ show: false, data: null, method: "none" });
            refetch();
          }}
        />
      )}
      
      {showCreateEnrollment.method === "edit" && (
        <EditEnrollment
          participant={programNumber}
          onClose={() => {
            setShowCreateEnrollment({ show: false, data: null, method: "none" });
            refetch();
          }}
          data={showCreateEnrollment.data}
          // refetch={refetch}
        />
      )}

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent className="bg-white p-6 rounded-lg shadow-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Enrollment Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this enrollment record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteEnrollment}
            >
              Delete
            </AlertDialogAction>
            <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
